/**
 * Экран 13 — верификатор. Главный экран продукта.
 *
 * Весь пересчёт идёт в браузере через @allswipes/fair: тот же алгоритм, что на
 * сервере, проверенный теми же векторами. Мы не спрашиваем сервер «всё верно?»
 * — мы считаем сами и сравниваем с опубликованным. Поэтому сид можно вставить
 * свой: взять его с сайта гослото, а не поверить нашей копии.
 */
import { ApiError } from '@allswipes/client';
import { checkDraw, checkTicketList, type DrawCheck, type VerifyBundle } from '@allswipes/fair';
import { Badge, Button, Card, Field, Hash, Icon, KeyRow, Skeleton, num } from '@allswipes/ui';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { api } from '../api';
import { Shell } from '../components/Shell';
import { useResource } from '../hooks';

import { useT } from '@allswipes/i18n';

export function VerifierScreen() {
  const t = useT();
  const { id = '' } = useParams();
  const bundle = useResource(() => api.verifyBundle(id), [id]);

  return (
    <Shell title={t('Верификатор')} subtitle={t('пересчитай результат сам, в своём браузере')}>
      {bundle.loading && <Skeleton count={2} height={140} />}
      {bundle.error && (
        <Card>
          <div className="h2">{t('Не загрузилось')}</div>
          <p className="sub" style={{ marginTop: 6 }}>{bundle.error}</p>
        </Card>
      )}
      {bundle.data && <Verifier bundle={bundle.data} campaignId={id} />}
    </Shell>
  );
}

function Verifier({ bundle, campaignId }: { bundle: VerifyBundle; campaignId: string }) {
  const t = useT();
  const [seed, setSeed] = useState(bundle.external_value ?? '');
  const [check, setCheck] = useState<DrawCheck | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [listCheck, setListCheck] = useState<{ matches: boolean; computedRoot: string } | null>(null);

  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      setCheck(await checkDraw(bundle, seed));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('Не удалось пересчитать'));
    } finally {
      setBusy(false);
    }
  };

  /**
   * Независимая проверка списка билетов: тянем публичный список и считаем
   * корень Меркла заново. Это закрывает вопрос «а был ли мой билет в списке,
   * по которому считали» без доверия к нашему же корню.
   */
  const verifyList = async () => {
    setBusy(true);
    setError(null);
    try {
      const numbers: string[] = [];
      const picks: number[] = [];
      let after = 0;
      for (let page = 0; page < 200; page++) {
        const chunk = await api.participants(campaignId, after, 200);
        if (chunk.participants.length === 0) break;
        numbers.push(...chunk.participants.map((p) => p.number));
        picks.push(...chunk.participants.map((p) => p.pick ?? 0));
        if (chunk.next === after) break;
        after = chunk.next;
        if (numbers.length >= bundle.ticket_count) break;
      }
      // У акций v2 цифры участников — часть замороженного списка, поэтому
      // корень считается по номерам вместе с ними.
      setListCheck(
        await checkTicketList(
          numbers,
          bundle.ticket_root ?? '',
          bundle.algo === 'v2' ? picks : undefined,
        ),
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('Не удалось проверить список'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="stack">
      <Card>
        <div className="row" style={{ marginBottom: 10 }}>
          <span className="h2">{bundle.campaign_name}</span>
          <span className="spacer" />
          <Badge tone={bundle.verifiable ? 'good' : undefined}>
            {bundle.verifiable ? t('сид раскрыт') : t('сид закрыт')}
          </Badge>
        </div>

        <p className="sub">{bundle.explain}</p>

        {bundle.legacy_algo && (
          <p className="sub" style={{ marginTop: 10, color: 'var(--warn)' }}>
            {t('Акция проведена учебным алгоритмом {algo}. Он небезопасен и больше не используется; пересчёт показан, чтобы исторический результат остался проверяемым.', { algo: bundle.algo })}
          </p>
        )}

        <div style={{ marginTop: 12 }}>
          <KeyRow label={t('Алгоритм')}>{bundle.algo}</KeyRow>
          <KeyRow label={t('Хеш сида сервера')} mono>
            <Hash value={bundle.commit} />
          </KeyRow>
          {bundle.server_seed && (
            <KeyRow label={t('Сид сервера')} mono>
              <Hash value={bundle.server_seed} />
            </KeyRow>
          )}
          {bundle.ticket_root && (
            <KeyRow label={t('Корень списка билетов')} mono>
              <Hash value={bundle.ticket_root} />
            </KeyRow>
          )}
          <KeyRow label={t('Билетов')}>{num(bundle.ticket_count)}</KeyRow>
          <KeyRow label={t('Призов')}>{num(bundle.winner_count)}</KeyRow>
          {bundle.external_source && <KeyRow label={t('Источник сида')}>{bundle.external_source}</KeyRow>}
        </div>
      </Card>

      {!bundle.verifiable && (
        <Card>
          <div className="h2">{t('Пересчитать пока нельзя')}</div>
          <p className="sub" style={{ marginTop: 6 }}>
            {t('Сид сервера раскрывается только после окончания акции. До этого опубликован его хеш — он доказывает, что сид уже существовал и не может измениться. Если бы сид был открыт сейчас, любой мог бы заранее посчитать, какие коды выигрышные.')}
          </p>
          <Link to={`/c/${campaignId}`} className="btn out block" style={{ marginTop: 12 }}>
            {t('Вернуться к акции')}
          </Link>
        </Card>
      )}

      {bundle.verifiable && (
        <Card>
          <Field
            label={t('Публичный сид — вставь из официального источника')}
            hint={t('Можно взять значение с сайта тиража и вставить сюда: пересчёт пойдёт по нему, а не по нашей копии.')}
          >
            <textarea
              className="textarea mono"
              rows={2}
              value={seed}
              onChange={(e) => setSeed(e.target.value)}
            />
          </Field>

          <Button variant="pri" block disabled={busy} onClick={run}>
            {busy ? t('Считаем…') : t('Пересчитать победителей')}
          </Button>

          {error && (
            <p className="sub" style={{ color: 'var(--bad)', marginTop: 10 }}>
              {error}
            </p>
          )}

          {check && (
            <div style={{ marginTop: 16 }}>
              <div className={`verdict ${check.matches ? '' : 'bad'}`}>
                <span className="vi">
                  <Icon name={check.matches ? 'check' : 'alert'} size={15} />
                </span>
                <div>
                  <div className="h2">
                    {check.matches ? t('Совпало полностью') : t('Результат не совпал')}
                  </div>
                  <div className="sub">
                    {check.matches
                      ? t('Пересчитанные победители совпадают с опубликованными, а сид — со своим хешем.')
                      : t('Пересчёт в твоём браузере дал другой результат. Ниже видно, где именно.')}
                  </div>
                </div>
              </div>

              {check.notes.map((note, i) => (
                <p key={i} className="sub" style={{ marginTop: 10, color: 'var(--warn)' }}>
                  {note}
                </p>
              ))}

              <div style={{ marginTop: 14 }}>
                <KeyRow label={t('Хеш сида сходится')}>{check.commitOk ? t('да') : t('НЕТ')}</KeyRow>
                <KeyRow label={t('Транскрипт')} mono>
                  <Hash value={check.contextHex} chars={12} />
                </KeyRow>
              </div>

              <table
                style={{ width: '100%', marginTop: 14, borderCollapse: 'collapse', fontSize: 13 }}
              >
                <thead>
                  <tr className="lab">
                    <th style={{ textAlign: 'left', padding: '6px 0' }}>#</th>
                    <th style={{ textAlign: 'left', padding: '6px 0' }}>{t('Опубликовано')}</th>
                    <th style={{ textAlign: 'left', padding: '6px 0' }}>{t('Пересчитано')}</th>
                  </tr>
                </thead>
                <tbody>
                  {check.computed.map((row, i) => {
                    const published = check.published[i];
                    const ok = published?.sequence === row.sequence;
                    return (
                      <tr key={row.position} style={{ borderTop: '1px solid var(--border)' }}>
                        <td className="mono faint" style={{ padding: '7px 0' }}>
                          {row.position}
                        </td>
                        <td className="mono" style={{ padding: '7px 0' }}>
                          {published?.number ?? '—'}
                        </td>
                        <td
                          className="mono"
                          style={{ padding: '7px 0', color: ok ? 'var(--head)' : 'var(--bad)' }}
                        >
                          №{row.sequence}{' '}
                          <Icon name={ok ? 'check' : 'close'} size={11} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {bundle.ticket_root && (
        <Card>
          <div className="h2">{t('Проверить список билетов')}</div>
          <p className="sub" style={{ marginTop: 6 }}>
            {t('Скачаем открытый список участников и посчитаем его корень Меркла заново. Если он совпадёт с опубликованным, значит розыгрыш считался именно по этому списку — и ни один билет не добавили и не убрали после фиксации.')}
          </p>
          <div style={{ marginTop: 12 }}>
            <Button variant="out" block disabled={busy} onClick={verifyList}>
              {busy ? t('Проверяем…') : t('Пересчитать корень списка')}
            </Button>
          </div>

          {listCheck && (
            <div className={`verdict ${listCheck.matches ? '' : 'bad'}`} style={{ marginTop: 14 }}>
              <span className="vi">
                <Icon name={listCheck.matches ? 'check' : 'alert'} size={15} />
              </span>
              <div>
                <div className="h2">{listCheck.matches ? t('Список тот же') : t('Корень не совпал')}</div>
                <div className="sub mono" style={{ fontSize: 11.5, marginTop: 4 }}>
                  {listCheck.computedRoot}
                </div>
              </div>
            </div>
          )}
        </Card>
      )}

      <Card>
        <div className="h2">{t('Как это работает')}</div>
        <ol className="steps" style={{ marginTop: 10 }}>
          <li className="st">
            <span className="n">1</span>
            <div>
              <div className="h2">{t('До старта')}</div>
              <div className="sub">
                {t('Сервер создаёт случайный сид и публикует его SHA-256. Сид секретный, хеш — нет.')}
              </div>
            </div>
          </li>
          <li className="st">
            <span className="n">2</span>
            <div>
              <div className="h2">{t('В конце приёма кодов')}</div>
              <div className="sub">
                {t('Список билетов фиксируется корнем Меркла и публикуется — до того, как станет известен публичный сид.')}
              </div>
            </div>
          </li>
          <li className="st">
            <span className="n">3</span>
            <div>
              <div className="h2">{t('В день розыгрыша')}</div>
              <div className="sub">
                {t('Объявляется внешний публичный сид, которым не управляет ни бренд, ни ALLSWIPES. Победители = HMAC-SHA256(сид сервера, публичный сид ‖ корень ‖ счётчик) с отбраковкой без смещения по модулю.')}
              </div>
            </div>
          </li>
          <li className="st">
            <span className="n">4</span>
            <div>
              <div className="h2">{t('После')}</div>
              <div className="sub">
                {t('Сид сервера раскрывается. Его хеш обязан совпасть с опубликованным до старта — значит, подменить сид было нельзя.')}
              </div>
            </div>
          </li>
        </ol>
      </Card>
    </div>
  );
}
